import React, { useState, useEffect } from 'react';
import { Search, Filter, Edit, RefreshCw, CheckCircle, XCircle, Ban, DollarSign, Eye, UserPlus, Download, Filter as FilterIcon, Users, TestTube, ToggleLeft, ToggleRight, ChevronDown, ChevronUp, Save, X as XIcon, Wallet, MessageSquare } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useTransactionSync } from '../../hooks/useTransactionSync';
import UserEditModal from '../../components/UserEditModal';
import UserDetailsModal from '../../components/Admin/UserDetailsModal';
import AdminStartChatModal from '../../components/Admin/AdminStartChatModal';
import CryptoIcon from '../../components/CryptoIcon';

const AdminUsers = () => {
  const { toast } = useToast();
  const { syncTrigger, triggerSync } = useTransactionSync();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [chatUser, setChatUser] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [userTypeFilter, setUserTypeFilter] = useState('all'); // 'all', 'real', 'test'
  const [kycFilter, setKycFilter] = useState('all'); // 'all', 'pending', 'approved', 'rejected'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'suspended', 'banned'
  const [showFilters, setShowFilters] = useState(false);
  const [kycSettings, setKycSettings] = useState({
    requires_kyc: false,
    kyc_enabled: false
  });
  const [updatingKyc, setUpdatingKyc] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userBalances, setUserBalances] = useState({});
  const [editingBalances, setEditingBalances] = useState({});
  const [savingBalances, setSavingBalances] = useState(false);
  const [cryptoPrices, setCryptoPrices] = useState({});

  useEffect(() => {
    fetchUsers();
    fetchKycSettings();
    fetchUnreadCounts();
  }, [search, userTypeFilter, kycFilter, statusFilter]);

  const fetchUnreadCounts = async () => {
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
      if (!token) return;
      const res = await fetch('/api/messages/admin/unread-counts-by-user', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadCounts(data.counts || {});
      }
    } catch (e) {
      // Silently ignore — badge is decorative, doesn't block the page.
    }
  };

  // Re-fetch when sync is triggered from other components
  useEffect(() => {
    if (syncTrigger > 0) {
      console.log('🔄 AdminUsers: Syncing due to transaction change');
      fetchUsers();
    }
  }, [syncTrigger]);

  const fetchKycSettings = async () => {
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
      const BACKEND_URL = '';
      
      const response = await fetch(`${BACKEND_URL}/api/platform/config/kyc-thresholds`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setKycSettings({
          requires_kyc: data.thresholds?.requires_kyc || false,
          kyc_enabled: true
        });
      }
    } catch (error) {
      console.error('Failed to fetch KYC settings:', error);
    }
  };

  const toggleKycRequirement = async () => {
    setUpdatingKyc(true);
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
      const BACKEND_URL = '';
      
      const response = await fetch(`${BACKEND_URL}/api/platform/config/kyc-thresholds`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          requires_kyc: !kycSettings.requires_kyc,
          daily_withdrawal_limit: 10000,
          monthly_withdrawal_limit: 15000,
          single_trade_limit: 7500
        })
      });

      if (response.ok) {
        setKycSettings(prev => ({ ...prev, requires_kyc: !prev.requires_kyc }));
        toast({
          title: 'Success',
          description: `KYC is now ${!kycSettings.requires_kyc ? 'required' : 'optional'} for registration`
        });
      } else {
        throw new Error('Failed to update KYC settings');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update KYC requirement',
        variant: 'destructive'
      });
    } finally {
      setUpdatingKyc(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
      const BACKEND_URL = '';
      
      if (!token) {
        toast({
          title: 'Error',
          description: 'No admin authentication token found',
          variant: 'destructive'
        });
        setLoading(false);
        return;
      }
      
      // Load ALL users in a single request (limit=1000 to get all)
      const params = new URLSearchParams({
        page: '1',
        limit: '1000'
      });
      
      if (search) params.append('search', search);
      if (kycFilter !== 'all') params.append('kyc_status', kycFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await fetch(
        `${BACKEND_URL}/api/admin/dashboard/users?${params}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        let filteredUsers = data.users || [];
        
        // Apply user type filter (filters are applied server-side for kyc and status)
        if (userTypeFilter !== 'all') {
          filteredUsers = filteredUsers.filter(u => 
            userTypeFilter === 'test' ? u.is_test_user : !u.is_test_user
          );
        }
        
        setUsers(filteredUsers);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to fetch users',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error',
        description: 'Failed to connect to server',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleSaveUser = () => {
    fetchUsers();
    setShowEditModal(false);
    setSelectedUser(null);
  };

  const fetchUserBalanceDetails = async (userId) => {
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
      const BACKEND_URL = '';
      
      const response = await fetch(
        `${BACKEND_URL}/api/admin/dashboard/users/${userId}/balance`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Transform data to match expected format
        setUserBalances(prev => ({
          ...prev,
          [userId]: {
            total_usdt: data.total_balance_usdt || 0,
            holdings: data.breakdown || []
          }
        }));
        
        // Also fetch current crypto prices
        const pricesResponse = await fetch(`${BACKEND_URL}/api/market/prices?type=crypto`);
        if (pricesResponse.ok) {
          const pricesData = await pricesResponse.json();
          const prices = {};
          pricesData.forEach(p => {
            prices[p.base_currency] = p.current_price;
          });
          setCryptoPrices(prices);
        }
      }
    } catch (error) {
      console.error('Error fetching balance details:', error);
    }
  };

  const toggleUserExpansion = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      setEditingBalances({});
    } else {
      setExpandedUser(userId);
      await fetchUserBalanceDetails(userId);
    }
  };

  const handleBalanceEdit = (userId, currency, value) => {
    setEditingBalances(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [currency]: parseFloat(value) || 0
      }
    }));
  };

  const cancelBalanceEdit = (userId) => {
    setEditingBalances(prev => ({ ...prev, [userId]: {} }));
    toast({
      title: 'Cancelled',
      description: 'Balance changes discarded'
    });
  };

  const saveUserBalances = async (userId) => {
    setSavingBalances(true);
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
      const BACKEND_URL = '';
      
      // Get current balances
      const currentBalances = userBalances[userId]?.holdings || [];
      
      // Merge with edited balances
      const newBalances = {};
      currentBalances.forEach(h => {
        const editedValue = editingBalances[userId]?.[h.currency];
        newBalances[h.currency] = editedValue !== undefined ? editedValue : h.amount;
      });
      
      const response = await fetch(
        `${BACKEND_URL}/api/admin/dashboard/users/${userId}/balance`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ balances: newBalances })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        toast({
          title: 'Success',
          description: `Balance updated successfully. New total: $${data.new_total_balance_usdt.toFixed(2)}`,
        });
        
        // Refresh user list and balance details
        await fetchUsers();
        await fetchUserBalanceDetails(userId);
        setEditingBalances(prev => ({ ...prev, [userId]: {} }));
        
        // Trigger sync for real-time update to user's view
        triggerSync();
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to update balance',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error saving balances:', error);
      toast({
        title: 'Error',
        description: 'Failed to save balance changes',
        variant: 'destructive'
      });
    } finally {
      setSavingBalances(false);
    }
  };

  // Note: handleBalanceEdit, saveUserBalances, and cancelBalanceEdit functions already defined above

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">User Management</h1>
          <p className="text-cyan-400">Manage users, KYC status, and account access</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.location.href = '/admin-portal/kyc-management'}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 hover:border-purple-500/50 text-purple-300 transition-all"
          >
            <CheckCircle className="w-4 h-4" />
            KYC Management
          </button>
          <button
            onClick={fetchUsers}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 transition-all"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* KYC Registration Control */}
      <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-500/20 rounded-lg">
              <CheckCircle className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                KYC Registration Requirement
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  kycSettings.requires_kyc 
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                    : 'bg-green-500/20 text-green-400 border border-green-500/30'
                }`}>
                  {kycSettings.requires_kyc ? 'REQUIRED' : 'OPTIONAL'}
                </span>
              </h3>
              <p className="text-purple-300 text-sm mt-1">
                {kycSettings.requires_kyc 
                  ? 'Users must complete KYC during registration to create an account'
                  : 'Users can register without KYC and complete it later from their profile'}
              </p>
            </div>
          </div>
          <button
            onClick={toggleKycRequirement}
            disabled={updatingKyc}
            className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
              kycSettings.requires_kyc
                ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30'
                : 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30'
            } disabled:opacity-50`}
          >
            {updatingKyc ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                {kycSettings.requires_kyc ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                Make {kycSettings.requires_kyc ? 'Optional' : 'Required'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-cyan-600" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by username or email..."
                className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-800 focus:outline-none focus:border-cyan-400"
              />
            </div>
          </div>

          {/* KYC Filter */}
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">KYC Status</label>
            <select
              value={kycFilter}
              onChange={(e) => setKycFilter(e.target.value)}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            >
              <option value="">All KYC Status</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-cyan-400 text-sm font-semibold mb-2">Account Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl overflow-hidden">
        {/* Scrollable table container with max height */}
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-slate-800 border-b border-cyan-500/30 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-4 text-left text-cyan-400 font-semibold w-12"></th>
                <th className="px-6 py-4 text-left text-cyan-400 font-semibold">Username</th>
                <th className="px-6 py-4 text-left text-cyan-400 font-semibold">Email</th>
                <th className="px-6 py-4 text-left text-cyan-400 font-semibold">Total Balance (USDT)</th>
                <th className="px-6 py-4 text-left text-cyan-400 font-semibold">KYC Status</th>
                <th className="px-6 py-4 text-left text-cyan-400 font-semibold">Status</th>
                <th className="px-6 py-4 text-left text-cyan-400 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-cyan-600">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <React.Fragment key={user.user_id}>
                    <tr className="border-b border-cyan-500/10 hover:bg-slate-800/50">
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          onClick={() => toggleUserExpansion(user.user_id || user._id)}
                          className="text-cyan-400 hover:text-cyan-300 transition-colors"
                        >
                          {expandedUser === (user.user_id || user._id) ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-white">
                        <div className="font-semibold">{user.username}</div>
                        <div className="text-xs text-gray-400">#{user.user_number || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 text-cyan-400">{user.email}</td>
                      <td className="px-6 py-4 text-white">
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-green-400" />
                          <span className="font-bold text-lg">{user.wallet_balance_usd?.toFixed(2) || '0.00'}</span>
                          <span className="text-xs text-gray-400">USDT</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-cyan-600 mt-1">
                          <span>USDT equiv of all holdings</span>
                          {user.using_live_prices && (
                            <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded text-[10px]">
                              Live
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          user.kyc_status === 'approved' || user.kyc_status === 'verified' ? 'bg-green-500/20 text-green-400' :
                          user.kyc_status === 'pending' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {user.kyc_status || 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          user.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {user.status || 'active'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="px-3 py-1 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 text-sm font-semibold transition-all flex items-center gap-1"
                            data-testid={`edit-user-btn-${user.user_id || user._id}`}
                          >
                            <Edit className="w-4 h-4" />
                            Edit Profile
                          </button>
                          <button
                            onClick={() => setChatUser(user)}
                            className="relative px-3 py-1 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 text-sm font-semibold transition-all flex items-center gap-1"
                            data-testid={`start-chat-btn-${user.user_id || user._id}`}
                            title="Start chat with user"
                          >
                            <MessageSquare className="w-4 h-4" />
                            Chat
                            {(() => {
                              const uid = user.user_id || user._id;
                              const n = unreadCounts[uid] || unreadCounts[String(uid)] || 0;
                              return n > 0 ? (
                                <span
                                  data-testid={`chat-unread-badge-${uid}`}
                                  className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border border-slate-900 shadow"
                                >
                                  {n > 99 ? '99+' : n}
                                </span>
                              ) : null;
                            })()}
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expandable Crypto Holdings Row */}
                    {expandedUser === (user.user_id || user._id) && (
                      <tr className="bg-slate-900/50">
                        <td colSpan="7" className="px-6 py-6">
                          <div className="space-y-4">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <Wallet className="w-5 h-5 text-cyan-400" />
                                <h3 className="text-lg font-bold text-white">Crypto Holdings</h3>
                              </div>
                              {Object.keys(editingBalances[user.user_id || user._id] || {}).length > 0 && (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => cancelBalanceEdit(user.user_id || user._id)}
                                    className="px-4 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 font-semibold transition-all flex items-center gap-2"
                                  >
                                    <XIcon className="w-4 h-4" />
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => saveUserBalances(user.user_id || user._id)}
                                    disabled={savingBalances}
                                    className="px-4 py-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 font-semibold transition-all flex items-center gap-2 disabled:opacity-50"
                                  >
                                    {savingBalances ? (
                                      <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Saving...
                                      </>
                                    ) : (
                                      <>
                                        <Save className="w-4 h-4" />
                                        Save Changes
                                      </>
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                            
                            {userBalances[user.user_id || user._id] ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {userBalances[user.user_id || user._id].holdings.map((holding) => {
                                  const userId = user.user_id || user._id;
                                  const isEditing = editingBalances[userId]?.[holding.currency] !== undefined;
                                  const editValue = editingBalances[userId]?.[holding.currency];
                                  const displayAmount = isEditing ? editValue : holding.amount;
                                  
                                  return (
                                    <div
                                      key={holding.currency}
                                      className="bg-gradient-to-br from-slate-800 to-slate-900 border border-cyan-500/20 rounded-lg p-4 hover:border-cyan-500/40 transition-all"
                                    >
                                      <div className="flex items-center gap-3 mb-3">
                                        <CryptoIcon symbol={holding.currency} size={32} />
                                        <div>
                                          <div className="text-white font-bold">{(holding.currency || '').toUpperCase()}</div>
                                          <div className="text-xs text-cyan-600">{holding.percentage}%</div>
                                        </div>
                                      </div>
                                      
                                      <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                          <label className="text-xs text-cyan-400">Amount:</label>
                                          <input
                                            type="number"
                                            step="0.00000001"
                                            value={displayAmount}
                                            onChange={(e) => handleBalanceEdit(userId, holding.currency, e.target.value)}
                                            className="flex-1 px-2 py-1 bg-slate-700 border border-cyan-500/30 rounded text-white text-sm focus:outline-none focus:border-cyan-400"
                                          />
                                        </div>
                                        
                                        <div className="text-xs text-gray-400">
                                          Value: ${holding.value_usdt?.toFixed(2) || '0.00'} USDT
                                        </div>
                                        
                                        <div className="text-xs text-gray-500">
                                          Price: ${cryptoPrices[holding.currency]?.toFixed(2) || holding.price?.toFixed(2) || '0.00'}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="flex justify-center py-8">
                                <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
                              </div>
                            )}
                            
                            {userBalances[user.user_id || user._id] && (
                              <div className="mt-4 p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <span className="text-cyan-400 font-semibold">Total Portfolio Value:</span>
                                  <span className="text-white text-xl font-bold">
                                    ${userBalances[user.user_id || user._id].total_usdt?.toFixed(2) || '0.00'} USDT
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Total Users Count */}
        {!loading && users.length > 0 && (
          <div className="px-6 py-4 border-t border-cyan-500/30 bg-slate-800/30">
            <div className="flex items-center justify-between">
              <span className="text-cyan-400 font-semibold">
                Total Users: {users.length}
              </span>
              <span className="text-gray-400 text-sm">
                Displaying all users in a single scrollable view
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <UserEditModal
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSave={handleSaveUser}
        />
      )}

      {/* Start Chat with User Modal */}
      {chatUser && (
        <AdminStartChatModal
          user={chatUser}
          onClose={() => {
            setChatUser(null);
            fetchUnreadCounts();
          }}
        />
      )}
    </div>
  );
};

export default AdminUsers;